<script setup lang="ts">
import { useInjectedColor } from '~/composables/useInjectedColor'
import type { VInputProps } from '~~/app/types/formProps'

const props = withDefaults(defineProps<VInputProps>(), {
  type: 'text',
  color: undefined,
  size: 'normal',
  autocomplete: undefined,
  inputMode: 'text',
  placeholder: undefined,
  step: undefined,
  ariaLabel: undefined
})

const modelValue = defineModel<any>({ required: true })

const input = ref<HTMLInputElement | null>(null)
const textArea = ref<HTMLTextAreaElement | null>(null)
defineExpose({
  input,
  textArea
})

const $field = useInjectedColor()

const { t, te } = useI18n()

const computedPlaceholder = computed(() =>
  props.placeholder && te(props.placeholder) ? t(props.placeholder) : props.placeholder
)

const computedAriaLabel = computed(() =>
  props.ariaLabel && te(props.ariaLabel) ? t(props.ariaLabel) : props.ariaLabel
)

const computedColor = computed(() => {
  let color = props.color
  if ($field.color?.value) {
    color = $field.color.value
  }

  if (!color) {
    let colorClasses = tw`border-default`
    if (!props.disabled && !props.readOnly) {
      colorClasses = twMerge(
        colorClasses,
        tw`transition-[border] duration-100 hover:border-accented`
      )
    }
    return colorClasses
  }

  return `border-${color}`
})
</script>

<template>
  <input
    v-if="type !== 'textarea'"
    ref="input"
    v-model="modelValue"
    class="input"
    :class="[computedColor, size]"
    :inputmode="inputMode"
    :readonly="readOnly"
    :type
    :placeholder="computedPlaceholder"
    :aria-label="computedAriaLabel"
    :autocomplete
    :tabindex
    :disabled
    :step
  />
  <textarea
    v-else
    ref="textArea"
    v-model="modelValue"
    class="textarea"
    :class="[computedColor, size]"
    :inputmode="inputMode"
    :readonly="readOnly"
    :placeholder="computedPlaceholder"
    :aria-label="computedAriaLabel"
    :autocomplete
    :disabled
  />
</template>

<style scoped></style>
